import { plainToClass } from "class-transformer";
import { CommunicationsListingDto } from "./communications.objects";
import { TableColumnVisibilityController, TableColumnGroup } from "../../../shared/scripts/table-column-visibility";
import { Unique } from "../../../shared/scripts/array-functions";
import { IFilter } from "../../../shared/scripts/filtering/filtering.entities";
import { InitializeFilterDropdown } from "../../../shared/scripts/filtering/filtering.utilities";
import { CommunicationDialog } from "./components/communications.dialog";
import { CommunicationDialogReadonly } from "./components/communications-readonly.dialog";
import { AuditHistoryDialog } from "../audit/scripts/audit-history.dialog";

declare global {
    interface Window {
        page: any;
    }
}

export class CommunicationsPage implements IFilter {
    communications: CommunicationsListingDto[];
    dataTable: any;
    filterDropdownIds: string[];
    communicationDialog: CommunicationDialog | CommunicationDialogReadonly;
    isNonViewer: boolean;

    constructor(allCommunicationsJson: any, allCommunicationTypesJson: any, allCommunicationTopicsJson: any, allCommunicationStatusesJson: any, allAttachmentTypesJson: any, allSitesJson: any, allContactsJson: any, allUsersJson: any, isNonViewer: boolean) {
        $(() => {
            this.communications = plainToClass(CommunicationsListingDto, <CommunicationsListingDto[]>allCommunicationsJson);
            this.filterDropdownIds = [
                '#type-dropdown', '#site-dropdown', '#study-dropdown', '#status-dropdown', '#createdby-dropdown'
            ];
            this.isNonViewer = isNonViewer === true;
            this.initializeFilters();
            this.initializeTable();

            // Instantiate dialog logic and pass required data
            const auditDialog = new AuditHistoryDialog();
            if (this.isNonViewer) {
                this.communicationDialog = new CommunicationDialog(
                    allCommunicationsJson,
                    allCommunicationTypesJson,
                    allCommunicationTopicsJson,
                    allCommunicationStatusesJson,
                    allAttachmentTypesJson,
                    allSitesJson,
                    allContactsJson,
                    allUsersJson,
                    auditDialog
                );
                this.communicationDialog.successCallback = this.saveSuccess.bind(this);
            } else {
                this.communicationDialog = new CommunicationDialogReadonly(
                    allCommunicationsJson,
                    allCommunicationTypesJson,
                    allCommunicationTopicsJson,
                    allCommunicationStatusesJson,
                    allAttachmentTypesJson,
                    allSitesJson,
                    allContactsJson,
                    allUsersJson
                );
            }
        });
    }

    initializeFilters(): void {
        // Type filter
        let typeValues: string[] = this.communications.map(c => c.Type);
        InitializeFilterDropdown('#type-dropdown', Unique(typeValues, true, ["None Selected"]), this);
        // Site filter
        let siteValues: string[] = [];
        this.communications.forEach(c => siteValues.push(...c.Site));
        InitializeFilterDropdown('#site-dropdown', Unique(siteValues, true, ["None Selected"]), this);
        // Study filter
        let studyValues: string[] = [];
        this.communications.forEach(c => studyValues.push(...c.Study));
        InitializeFilterDropdown('#study-dropdown', Unique(studyValues, true, ["None Selected"]), this);
        // Status filter
        let statusValues: string[] = this.communications.map(c => c.Status);
        InitializeFilterDropdown('#status-dropdown', Unique(statusValues, true, ["None Selected"]), this);
        // CreatedBy filter
        let createdByValues: string[] = this.communications.map(c => c.CreatedBy).filter(v => v && v.trim() !== '');
        InitializeFilterDropdown('#createdby-dropdown', Unique(createdByValues, true, ["None Selected"]), this);
    }

    initializeTable(): void {
        this.dataTable = $('#communications-table').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            order: [[0, 'asc']], // Order by Date
            columns: [
                { data: "Date", title: "Date" },
                { data: "Type", title: "Type" },
                { data: "Category", title: "Category" },
                { data: "Site", title: "Site", render: (data) => Array.isArray(data) ? data.join(", ") : data },
                { data: "Study", title: "Study", render: (data) => Array.isArray(data) ? data.slice().sort().join(", ") : data },
                { data: "Significant", title: "Significant", render: (data) => data ? "Significant" : "" },
                { data: "Status", title: "Status" },
                { data: "CreatedBy", title: "Created By" },
                {
                    data: null,
                    title: "",
                    className: "ctms-table-col-actions",
                    orderable: false,
                    render: function () {

                        //return '<button type="button" title="Edit"><i class="fa-solid fa-pencil" aria-hidden="true"></i></button>';
                        return '<button><i class="ctms-icon-pencil"></i></button>';
                    }
                }
            ],
            data: this.communications
        });

        $('#custom-dt-search').on('input', function () {
            $('#communications-table').DataTable().search(String($(this).val() ?? '')).draw();
        });
        $('.ctms-icon-button[title="Clear Search"]').on('click', function () {
            $('#custom-dt-search').val('');
            $('#communications-table').DataTable().search('').draw();
        });
    }

    updateTable(matchingItems: CommunicationsListingDto[]): void {
        this.dataTable.clear();
        this.dataTable.rows.add(matchingItems).draw();
    }

    triggerFilter(): void {
        const selectedType = this.getSelectedDropdownValues('#type-dropdown');
        const selectedSite = this.getSelectedDropdownValues('#site-dropdown');
        const selectedStudy = this.getSelectedDropdownValues('#study-dropdown');
        const selectedStatus = this.getSelectedDropdownValues('#status-dropdown');
        const selectedCreatedBy = this.getSelectedDropdownValues('#createdby-dropdown');

        const matchingItems: CommunicationsListingDto[] = [];
        this.communications.forEach(item => {
            if (item.matchesFilter(selectedType, selectedSite, selectedStudy, selectedStatus, selectedCreatedBy)) {
                matchingItems.push(item);
            }
        });
        this.updateTable(matchingItems);
    }

    getSelectedDropdownValues(selectId: string): any {
        return $(selectId).val();
    }

    resetFilters(): void {
        this.filterDropdownIds.forEach((dropdownId: string) => {
            $(dropdownId).selectpicker('selectAll');
            $(dropdownId).selectpicker('refresh');
        });
        $('#custom-dt-search').val('');
        this.dataTable.search('').draw();
        this.updateTable(this.communications);
    }

    saveSuccess(response: any) {
        if (response && response.success && response.communications) {
            const pascalCaseCommunications = mapKeysToPascalCase(response.communications);
            this.communications = plainToClass(CommunicationsListingDto, <CommunicationsListingDto[]>pascalCaseCommunications);
            this.filterDropdownIds.forEach(id => {
                $(id).selectpicker('destroy');
                $(id).empty();
            });
            this.resetFilters();
            this.updateTable(this.communications);
            this.initializeFilters();
        }

    }
}

$(document).on('click', '#communications-table .ctms-icon-pencil', function () {
    var row = $(this).closest('tr');
    var data = $('#communications-table').DataTable().row(row).data();
    if (!data || !data.Id) return;
    if (this.isNonViewer) {
        $('#newCommunicationEventDialogLabel').text('Edit Communication Event');
    }
    else {
        $('#newCommunicationEventDialogLabel').text('View Communication Event');
    }
    $.get('/secure/ctms/communications/communications?handler=CommunicationEventDto&id=' + data.Id, function (response) {
        if (response && response.success && response.communicationEvent) {
            if (window.page && window.page.communicationDialog) {
                $('#newCommunicationEventDialog').modal('show');
                window.page.communicationDialog.populateDialogFieldsFromDto(response.communicationEvent);
            }
        } else {
            alert('Could not load communication event for editing.');
        }
    });
});

$('#newCommunicationEventBtn').on('click', function () {
    $('#newCommunicationEventDialogLabel').text('New Communication Event');
    $('#communicationId').val(0);
});

function mapKeysToPascalCase(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(mapKeysToPascalCase);
    }
    if (obj !== null && typeof obj === 'object') {
        const newObj: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
                newObj[pascalKey] = mapKeysToPascalCase(obj[key]);
            }
        }
        return newObj;
    }
    return obj;
}